/**
 * Advanced Query Facet - Frontend Interactivity
 *
 * Handles accordion toggle behavior and button group interactions for
 * the Advanced Query Facet block. Provides accessible keyboard navigation
 * and visual feedback for filter selections.
 *
 * @package WAEPA
 * @since 1.0.0
 */

(function () {
	/**
	 * Initialize facet interactivity
	 *
	 * Sets up event listeners for accordion toggles and button group clicks.
	 * Runs on DOM ready to ensure all elements are available.
	 *
	 * @since 1.0.0
	 */
	function initFacets() {
		const facets = document.querySelectorAll(".advanced-query-facet");

		facets.forEach((facet) => {
			const buttons = facet.querySelectorAll(
				".advanced-query-facet__button:not(.advanced-query-facet__button--all)"
			);

			// Initialize button group interactions
			if (buttons.length > 0) {
				buttons.forEach((button) => {
					initButtonClick(button);
				});
			}
			initMobileToggle(facet);

			// Initialize "All Topics" button interactions
			const allButtons = facet.querySelectorAll(
				".advanced-query-facet__button--all"
			);
			allButtons.forEach((button) => {
				initAllButtonClick(button);
			});
		});
	}

	function initMobileToggle(facet) {
		const buttonGroup = facet.querySelector(
			".advanced-query-facet__button-group"
		);
		const mobileToggle = facet.querySelector(
			".advanced-query-facet__mobile-toggle"
		);
		if (!buttonGroup || !mobileToggle) return;

		function updateToggleText() {
			const activeBtn = buttonGroup.querySelector(
				".advanced-query-facet__button--active .advanced-query-facet__button-text"
			);
			mobileToggle.querySelector(
				".advanced-query-facet__mobile-toggle-text"
			).textContent = activeBtn ? activeBtn.textContent : "All Topics";
		}

		function handleResize() {
			if (window.innerWidth < 768) {
				mobileToggle.style.display = "";
				updateToggleText();
			} else {
				mobileToggle.style.display = "none";
				buttonGroup.classList.remove(
					"advanced-query-facet__button-group--opened"
				);
				mobileToggle.setAttribute("aria-expanded", "false");
			}
		}

		mobileToggle.addEventListener("click", () => {
			const opened = buttonGroup.classList.toggle(
				"advanced-query-facet__button-group--opened"
			);
			mobileToggle.setAttribute(
				"aria-expanded",
				opened ? "true" : "false"
			);
		});

		// Update text when selection changes
		buttonGroup.addEventListener("change", updateToggleText);

		window.addEventListener("resize", handleResize);
		handleResize();
		updateToggleText();
	}

	/**
	 * Initialize "All Topics" button click behavior
	 *
	 * Handles "All Topics" button clicks to reset only the checkboxes
	 * within the same button group facet, not all facets.
	 *
	 * @since 1.0.0
	 *
	 * @param {HTMLElement} button The "All Topics" button element
	 */
	function initAllButtonClick(button) {
		button.addEventListener("click", (event) => {
			event.preventDefault();

			// Find the parent button group container
			const buttonGroup = button.closest(
				".advanced-query-facet__button-group"
			);

			if (buttonGroup) {
				// Uncheck all checkboxes in this button group
				const checkboxes = buttonGroup.querySelectorAll(
					'input[type="checkbox"]'
				);
				let hasChecked = false;

				checkboxes.forEach((checkbox) => {
					if (checkbox.checked) {
						hasChecked = true;
						checkbox.checked = false;
					}
				});

				// Only trigger change if something was actually unchecked
				if (hasChecked && checkboxes.length > 0) {
					// Dispatch change event on the first checkbox to trigger form update
					checkboxes[0].dispatchEvent(
						new Event("change", { bubbles: true })
					);
				}
			}
		});
	}

	/**
	 * Initialize button click behavior
	 *
	 * Handles button clicks to toggle checkbox state and update visual
	 * appearance. Supports keyboard interaction via label click.
	 *
	 * @since 1.0.0
	 *
	 * @param {HTMLElement} button The button label element
	 */
	function initButtonClick(button) {
		const input = button.querySelector('input[type="checkbox"]');

		if (!input || input.disabled) {
			return;
		}

		// Handle click on label (which includes the hidden checkbox)
		button.addEventListener("click", (event) => {
			// Prevent double-firing (label click triggers input change automatically)
			// We just need to update the visual state
			updateButtonState(button, input.checked);
		});

		// Handle direct keyboard interaction on the input
		input.addEventListener("change", () => {
			updateButtonState(button, input.checked);
		});

		// Initialize state on page load
		updateButtonState(button, input.checked);
	}

	/**
	 * Update button visual state
	 *
	 * Adds or removes the active class based on checkbox state.
	 * Provides visual feedback for selected filters.
	 *
	 * @since 1.0.0
	 *
	 * @param {HTMLElement} button  The button label element
	 * @param {boolean}     checked Whether the checkbox is checked
	 */
	function updateButtonState(button, checked) {
		if (checked) {
			button.classList.add("advanced-query-facet__button--active");
		} else {
			button.classList.remove("advanced-query-facet__button--active");
		}
	}

	// Initialize on DOM ready
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initFacets);
	} else {
		// DOM is already ready
		initFacets();
	}

	// Re-initialize after AJAX updates (when facet HTML is replaced)
	document.addEventListener("advanced-query:reload", initFacets);
})();
